import React from 'react';
import {
  FaChartLine,
  FaExchangeAlt,
  FaUserShield,
  FaUsers,
  FaTrophy,
  FaCalculator,
  FaLayerGroup
} from 'react-icons/fa';

export const fintechMetadata = {
  id: 'fintech-payments',
  title: "Payment Analytics & Risk",
  company: "PayFlow",
  category: "Fintech Analytics",
  cardDescription: "Analyze multi-currency transaction data for a digital payments platform. Master TPV calculations, risk analysis, and cohort tracking.",
  learningObjectives: [
    "Currency Normalization (FX)",
    "Net TPV Calculations",
    "Risk & Chargeback Analysis",
    "Customer Cohort Modelling"
  ],
  scenario: (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-white dark:bg-[#0f172a] rounded-xl border border-gray-100 dark:border-gray-800 p-8 shadow-sm relative overflow-hidden group hover:border-emerald-500/20 transition-all duration-500">
        <div className="relative z-10">
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
            PayFlow <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Analytics</span>
          </h3>

          <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg max-w-3xl font-light">
            You've joined <strong className="text-gray-900 dark:text-white font-semibold">PayFlow</strong> as a Finance Data Analyst.
            The CFO needs insights on transaction performance, refund/chargeback impact, and customer cohort behaviour-all normalized to GBP.
          </p>
        </div>
        {/* Subtle Brand Accent */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/5 to-teal-500/5 rounded-full blur-2xl -mr-10 -mt-10"></div>
      </div>

      {/* Schema Grid */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4 pl-1">
          Data Architecture
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { table: 'transactions', desc: 'Payment records (multi-currency)' },
            { table: 'refunds', desc: 'Refund requests linked to txns' },
            { table: 'chargebacks', desc: 'Disputed transactions' },
            { table: 'fx_rates', desc: 'Daily currency rates to GBP' }
          ].map((item) => (
            <div key={item.table} className="bg-gray-50 dark:bg-[#162032] p-4 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
              <div className="font-mono font-bold text-emerald-600 dark:text-emerald-400 text-sm mb-1">
                {item.table}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {item.desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  ),
  introduction: ``,
  hasSQL: true
};

export const fintechChallenges = [
  {
    id: 'monthly-gross-tpv',
    title: 'Monthly Gross TPV',
    question: `- Join \`transactions\` to \`fx_rates\` on **both date and currency** to convert amounts to GBP
- Filter to \`status = 'success'\` only, and group by calendar month
- Return \`month\`, \`total_gross_tpv_gbp\`, \`transaction_count\`, and \`active_customers\` (distinct customers per month)`,
    keyConcepts: ['JOIN', 'DATE functions', 'SUM()', 'COUNT()', 'currency conversion'],
    defaultQuery: `-- TIP: DATE_TRUNC('month', ts) rounds a timestamp down to calendar month
-- SUM(amount * fx_rate) converts transactions to a single currency`,
    expectedColumns: [
      { name: 'month', type: 'DATE' },
      { name: 'total_gross_tpv_gbp', type: 'DECIMAL' },
      { name: 'transaction_count', type: 'INTEGER' },
      { name: 'active_customers', type: 'INTEGER' },
    ],
    businessRequest: `The CFO needs monthly Gross TPV in GBP for the board report. This requires converting all multi-currency transactions to a single reporting currency using daily FX rates.`,
    concept: `**Currency normalisation via a two-condition JOIN**

An FX rates table typically has one row per *date + currency pair*. That means the join needs two conditions - if you only join on currency, you'll match every historical rate for that currency and multiply your row count.

\`\`\`sql
JOIN fx_rates
  ON DATE(transaction_time) = fx_rates.date
 AND transactions.currency  = fx_rates.currency
\`\`\`

Once joined, converting to GBP is just multiplication inside a \`SUM\`:

\`\`\`sql
SUM(amount * rate_to_gbp)
\`\`\`

**DATE_TRUNC** rounds a timestamp down to the start of the period you want to group by:

\`\`\`sql
DATE_TRUNC('month', transaction_time)  -- 2024-03-15 09:00 → 2024-03-01
DATE_TRUNC('week',  transaction_time)  -- rounds to nearest Monday
\`\`\`

A missing join condition won't throw an error - it will silently return inflated numbers. Always do a quick spot-check on a single known transaction before trusting aggregated output.`,
    context: `**The Business Request**

The CFO needs monthly Gross TPV (Total Payment Volume) in GBP for the board report. This requires converting all multi-currency transactions to a single reporting currency.

**The Concept**

Currency normalization is fundamental in fintech. You must:

1. **JOIN** transactions to fx_rates on both date AND currency
2. **Filter** for successful transactions only
3. **Multiply** amount by \`rate_to_gbp\`
4. **Aggregate** by month

**The Syntax**

\`\`\`sql
SELECT 
  DATE_TRUNC('month', ...) AS month,
  SUM(...) AS total_gross_tpv_gbp,
  COUNT(...) AS transaction_count,
  COUNT(DISTINCT ...) AS active_customers
FROM transactions
JOIN fx_rates ON ... AND ...
WHERE ...
GROUP BY ...
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Calculate monthly Gross TPV in GBP. Return: \`month\`, \`total_gross_tpv_gbp\`, \`transaction_count\`, \`active_customers\`. Only include successful transactions.

</div>`,
    hint: `**Key steps:**

1. JOIN transactions to fx_rates on DATE(transaction_time) = date AND currency = currency
2. Filter WHERE status = 'success'
3. Multiply amount × \`rate_to_gbp\`
4. GROUP BY month using DATE_TRUNC
5. Count distinct customers for active_customers`,
    solution: `SELECT 
  DATE_TRUNC('month', transaction_time) AS month,
  ROUND(SUM(amount * rate_to_gbp), 2) AS total_gross_tpv_gbp,
  COUNT(*) AS transaction_count,
  COUNT(DISTINCT customer_id) AS active_customers
FROM transactions
JOIN fx_rates ON DATE(transaction_time) = fx_rates.date 
  AND transactions.currency = fx_rates.currency
WHERE status = 'success'
GROUP BY DATE_TRUNC('month', transaction_time)
ORDER BY month`,
    tip: `Always verify your FX join is correct by checking a few rows manually. A missing join condition can silently corrupt your financial data!`,
    explanation: `**Gross TPV Calculated**

**What Just Happened:**

1. **Joined Data:** You connected \`transactions\` to \`fx_rates\` using both date AND currency.
2. **Normalized:** Every transaction amount was multiplied by its daily \`rate_to_gbp\`.
3. **Aggregated:** You summed these normalized amounts by month.

**How It Works:**

| Component | Purpose | Example |
|-----------|---------|---------|
| **Source Amount** | Original transaction value | €100 (EUR) |
| **FX Rate** | Conversion factor to GBP | 0.85 (EUR to GBP) |
| **Normalized** | Reporting value | £85 (GBP) |
| **Gross TPV** | Sum of all normalized values | Total Sales Volume |

**Business Context:**

**Gross TPV (Total Payment Volume)** is the top-line metric for any payments business. It represents the total flow of money through the platform before any refunds or fees. Investors look at this first to gauge scale.`,
    concepts: [
      {
        icon: <FaExchangeAlt className="text-3xl text-[#2563eb]" />,
        title: 'Currency Conversion',
        description: 'Join to FX rates table on date AND currency to normalize multi-currency data to a single reporting currency.'
      },
      {
        icon: <FaChartLine className="text-3xl text-[#10b981]" />,
        title: 'TPV Metrics',
        description: 'Total Payment Volume is the headline metric for payment platforms. Track it monthly to show growth.'
      }
    ]
  },
  {
    id: 'refund-chargeback-impact',
    title: 'Refund & Chargeback Impact',
    question: `- Extend the previous query to add refunds and chargebacks, both joined via \`transaction_id\`
- Keep the \`fx_rates\` join for currency conversion
- Return \`month\`, \`gross_tpv_gbp\`, \`total_refunds_gbp\`, \`total_chargebacks_gbp\`, \`net_tpv_gbp\` (gross minus both), \`refund_rate\`, and \`chargeback_rate\` rounded to 2 decimal places`,
    keyConcepts: ['LEFT JOIN', 'COALESCE', 'multiple aggregations', 'rate calculations'],
    defaultQuery: `-- TIP: Net TPV = Gross - Refunds - Chargebacks
-- Use SUM(CASE WHEN transaction_type = 'refund' THEN amount ELSE 0 END)`,
    expectedColumns: [
      { name: 'month', type: 'DATE' },
      { name: 'gross_tpv_gbp', type: 'DECIMAL' },
      { name: 'total_refunds_gbp', type: 'DECIMAL' },
      { name: 'total_chargebacks_gbp', type: 'DECIMAL' },
      { name: 'net_tpv_gbp', type: 'DECIMAL' },
      { name: 'refund_rate', type: 'DECIMAL' },
      { name: 'chargeback_rate', type: 'DECIMAL' },
    ],
    businessRequest: `The CFO needs to understand how refunds and chargebacks erode Gross TPV each month. Net TPV = Gross TPV - Refunds - Chargebacks, and both rates must stay within card-network thresholds.`,
    concept: `**LEFT JOIN and COALESCE for optional related records**

Not every transaction will have a refund or a chargeback. If you use \`INNER JOIN\` to the refunds table, transactions with no refund drop from the result entirely - so your gross TPV would only include refunded transactions, which is wrong.

\`LEFT JOIN\` keeps all rows from the left table and fills the right side with \`NULL\` when there's no match. But \`NULL\` inside a \`SUM\` propagates: \`SUM\` of a column that's entirely \`NULL\` returns \`NULL\`, not \`0\`. \`COALESCE\` fixes that:

\`\`\`sql
COALESCE(SUM(some_nullable_column), 0)
-- returns 0 instead of NULL when no rows matched
\`\`\`

**Two-step approach** using a CTE keeps the arithmetic clean: compute the raw sums in the CTE, then derive \`net_tpv\` and the rate percentages in the outer query where the column names already exist.

**Industry thresholds to know:** card networks (Visa, Mastercard) can fine or suspend a merchant whose chargeback rate exceeds 1%. That makes this query a compliance tool, not just a reporting one.`,
    context: `**The Business Request**

The CFO needs to understand how refunds and chargebacks impact the bottom line. Net TPV = Gross TPV - Refunds - Chargebacks.

**The Concept**

This requires joining three tables and computing multiple metrics:

1. **Gross TPV** from successful transactions
2. **Refunds** linked to transactions
3. **Chargebacks** linked to transactions
4. **Net TPV** = Gross - Refunds - Chargebacks
5. **Risk rates** as percentages

**The Syntax**

\`\`\`sql
SELECT 
  month,
  gross_tpv,
  total_refunds,
  total_chargebacks,
  ... AS net_tpv,
  ROUND(...) AS refund_rate,
  ROUND(...) AS chargeback_rate
FROM (subquery)
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Compute monthly: \`gross_tpv_gbp\`, \`total_refunds_gbp\`, \`total_chargebacks_gbp\`, \`net_tpv_gbp\`, \`refund_rate\`, \`chargeback_rate\`.

</div>`,
    hint: `**Structure:**

1. Start with transactions (successful only)
2. LEFT JOIN refunds on \`transaction_id\`
3. LEFT JOIN chargebacks on \`transaction_id\`
4. JOIN fx_rates for currency conversion
5. Use \`COALESCE(SUM(refund_amount * rate_to_gbp), 0)\` to handle NULLs
6. Calculate rates as (deduction / gross) * 100`,
    solution: `WITH monthly_data AS (
  SELECT 
    DATE_TRUNC('month', t.transaction_time) AS month,
    SUM(t.amount * fx.rate_to_gbp) AS gross_tpv_gbp,
    COALESCE(SUM(r.refund_amount * fx.rate_to_gbp), 0) AS total_refunds_gbp,
    COALESCE(SUM(c.amount * fx.rate_to_gbp), 0) AS total_chargebacks_gbp
  FROM transactions t
  JOIN fx_rates fx ON DATE(t.transaction_time) = fx.date AND t.currency = fx.currency
  LEFT JOIN refunds r ON t.transaction_id = r.transaction_id
  LEFT JOIN chargebacks c ON t.transaction_id = c.transaction_id
  WHERE t.status = 'success'
  GROUP BY DATE_TRUNC('month', t.transaction_time)
)
SELECT 
  month,
  ROUND(gross_tpv_gbp, 2) AS gross_tpv_gbp,
  ROUND(total_refunds_gbp, 2) AS total_refunds_gbp,
  ROUND(total_chargebacks_gbp, 2) AS total_chargebacks_gbp,
  ROUND(gross_tpv_gbp - total_refunds_gbp - total_chargebacks_gbp, 2) AS net_tpv_gbp,
  ROUND(100.0 * total_refunds_gbp / gross_tpv_gbp, 2) AS refund_rate,
  ROUND(100.0 * total_chargebacks_gbp / gross_tpv_gbp, 2) AS chargeback_rate
FROM monthly_data
ORDER BY month`,
    tip: `Industry benchmarks: Refund rate < 5% is healthy, chargeback rate < 1% is critical for card network compliance. Higher rates trigger penalties.`,
    explanation: `**Financial Impact Analysis**

**What Just Happened:**

You transformed raw transaction data into a complete Profit & Loss (P&L) view. By left-joining refunds and chargebacks, you calculated exactly how much volume was lost.

**How It Works:**

| Metric | Formula | Healthy Range |
|--------|---------|---------------|
| **Gross TPV** | Sum of successful sales | N/A (Higher is better) |
| **Net TPV** | Gross - Refunds - Chargebacks | N/A (Keep close to Gross) |
| **Refund Rate** | (Refunds / Gross) * 100 | **< 5%** |
| **Chargeback Rate** | (Chargebacks / Gross) * 100 | **< 1%** (Critical) |

**Business Context:**

**Net TPV** is your true revenue base. A high Gross TPV means nothing if you lose 20% of it to refunds.
**Chargeback Risk:** Card networks (Visa, Mastercard) will fine or ban merchants whose chargeback rate exceeds 1%. Determining this rate accurately is a survival metric.`,
    concepts: [
      {
        icon: <FaCalculator className="text-3xl text-[#f97316]" />,
        title: 'Net TPV',
        description: 'Net TPV = Gross TPV - Refunds - Chargebacks. This is the actual revenue retained by the platform.'
      },
      {
        icon: <FaUserShield className="text-3xl text-[#ef4444]" />,
        title: 'Risk Rates',
        description: 'Chargeback rates > 1% trigger card network penalties. Monitoring these rates is critical for platform survival.'
      }
    ]
  },
  {
    id: 'customer-monthly-performance',
    title: 'Customer Monthly Performance Dataset',
    question: `- Same structure as the previous challenge, but add \`customer_id\` as an additional grouping dimension
- Use \`transactions\`, \`fx_rates\`, \`refunds\`, and \`chargebacks\`
- Return \`customer_id\`, \`month\`, \`gross_tpv_gbp\`, \`refund_total_gbp\`, \`chargeback_total_gbp\`, \`net_tpv_gbp\`, and \`transaction_count\`, ordered by month then customer`,
    keyConcepts: ['GROUP BY multiple columns', 'LEFT JOIN', 'reusable datasets', 'customer analytics'],
    defaultQuery: `-- TIP: GROUP BY customer_id, DATE_TRUNC('month', ts) for per-customer monthly rows
-- SELECT multiple aggregates (gross, net, count) in one pass`,
    expectedColumns: [
      { name: 'customer_id', type: 'TEXT' },
      { name: 'month', type: 'DATE' },
      { name: 'gross_tpv_gbp', type: 'DECIMAL' },
      { name: 'refund_total_gbp', type: 'DECIMAL' },
      { name: 'chargeback_total_gbp', type: 'DECIMAL' },
      { name: 'net_tpv_gbp', type: 'DECIMAL' },
      { name: 'transaction_count', type: 'INTEGER' },
    ],
    businessRequest: `The CFO needs a customer-level view of monthly financial performance. This dataset will power all subsequent risk analysis and cohort tracking.`,
    concept: `**Increasing granularity by adding a GROUP BY dimension**

The previous challenge gave you one row per month. Adding \`customer_id\` to \`GROUP BY\` simply slices each month into as many rows as there are customers - one row per customer-month combination.

\`\`\`sql
GROUP BY customer_id, DATE_TRUNC('month', transaction_time)
-- previously: GROUP BY DATE_TRUNC('month', transaction_time)
\`\`\`

This is a general principle: adding a column to \`GROUP BY\` increases granularity; removing one collapses it. The aggregates (\`SUM\`, \`COUNT\`) stay unchanged.

**Why this dataset matters:** a customer-month table is the standard foundation for downstream analytics. Risk flagging, cohort analysis, and lifetime value calculations all start from "here's what each customer did each month." Rather than rebuilding the joins every time, wrap this in a CTE once and reference it across subsequent queries - which is exactly what the next challenges do.`,
    context: `**The Business Request**

The CFO needs a customer-level view of monthly performance. This dataset will power all subsequent risk and cohort analysis.

**The Concept**

Building reusable analytical datasets is a core skill. This query calculates per-customer, per-month:

- Gross TPV
- Refund total
- Chargeback total
- Net TPV
- Transaction count

**The Syntax**

\`\`\`sql
SELECT 
  customer_id,
  DATE_TRUNC('month', ...) AS month,
  SUM(...) AS gross_tpv,
  COALESCE(...) AS refund_total,
  COALESCE(...) AS chargeback_total,
  ... AS net_tpv,
  COUNT(*) AS transaction_count
FROM transactions
LEFT JOIN refunds ON ...
LEFT JOIN chargebacks ON ...
JOIN fx_rates ON ...
GROUP BY customer_id, ...
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Create a customer-month dataset with: \`customer_id\`, \`month\`, \`gross_tpv_gbp\`, \`refund_total_gbp\`, \`chargeback_total_gbp\`, \`net_tpv_gbp\`, \`transaction_count\`.

</div>`,
    hint: `**Key insight:**

This is the same joins as Q2, but GROUP BY \`customer_id\` AND month instead of just month. Each row represents one customer's activity in one month.`,
    solution: `SELECT 
  t.customer_id,
  DATE_TRUNC('month', t.transaction_time) AS month,
  ROUND(SUM(t.amount * fx.rate_to_gbp), 2) AS gross_tpv_gbp,
  ROUND(COALESCE(SUM(r.refund_amount * fx.rate_to_gbp), 0), 2) AS refund_total_gbp,
  ROUND(COALESCE(SUM(c.amount * fx.rate_to_gbp), 0), 2) AS chargeback_total_gbp,
  ROUND(SUM(t.amount * fx.rate_to_gbp) - COALESCE(SUM(r.refund_amount * fx.rate_to_gbp), 0) - COALESCE(SUM(c.amount * fx.rate_to_gbp), 0), 2) AS net_tpv_gbp,
  COUNT(*) AS transaction_count
FROM transactions t
JOIN fx_rates fx ON DATE(t.transaction_time) = fx.date AND t.currency = fx.currency
LEFT JOIN refunds r ON t.transaction_id = r.transaction_id
LEFT JOIN chargebacks c ON t.transaction_id = c.transaction_id
WHERE t.status = 'success'
GROUP BY t.customer_id, DATE_TRUNC('month', t.transaction_time)
ORDER BY month, customer_id`,
    tip: `This customer-month dataset is a common pattern in analytics. Save this as a CTE or view-you'll reuse it for cohorts, risk scoring, and lifetime value calculations.`,
    explanation: `**Customer Dataset Generated**

**What Just Happened:**

You created a granular dataset where every row represents one customer's performance for one specific month. This "Customer-Month" granularity is the gold standard for downstream analytics.

**How It Works:**

Your dataset now contains everything needed for advanced modeling:

| Column | Source | Value |
|--------|--------|-------|
| \`customer_id\` | Grouping Key | Who is the actor? |
| \`month\` | Time Dimension | When did it happen? |
| \`net_tpv_gbp\` | Calculated | How valuable were they? |
| \`trans_count\` | Calculated | How active were they? |

**Business Application:**

This dataset is reusable. You can now simply query *this* result to answer complex questions like:
- "Who churned last month?"
- "Who is a high-value VIP?"
- "Who is abusing the refund policy?"`,
    concepts: [
      {
        icon: <FaLayerGroup className="text-3xl text-[#2563eb]" />,
        title: 'Analytical Datasets',
        description: 'Build customer-level datasets that aggregate key metrics. These become the foundation for all downstream analysis.'
      },
      {
        icon: <FaUsers className="text-3xl text-[#10b981]" />,
        title: 'Customer Granularity',
        description: 'Per-customer metrics enable risk scoring, cohort tracking, and personalized business decisions.'
      }
    ]
  },
  {
    id: 'high-risk-customers',
    title: 'Identify High-Risk Customers',
    question: `- Use the customer-month CTE from the previous challenge to flag months where chargeback rate > 5%, refund rate > 10%, or \`net_tpv_gbp\` is negative
- Aggregate per customer: count \`flagged_months\` and sum \`total_net_tpv_gbp\` across all months
- Return only customers with at least one flagged month, ordered by \`flagged_months\` descending`,
    keyConcepts: ['CTE', 'CASE WHEN', 'risk thresholds', 'customer flagging'],
    defaultQuery: `-- TIP: HAVING filters groups after aggregation
-- COUNT months where (refunds + chargebacks) exceed threshold, then HAVING count > 1`,
    expectedColumns: [
      { name: 'customer_id', type: 'TEXT' },
      { name: 'flagged_months', type: 'INTEGER' },
      { name: 'total_net_tpv_gbp', type: 'DECIMAL' },
    ],
    businessRequest: `The CFO wants a targeted risk queue: customers who breached chargeback (> 5%), refund (> 10%), or profitability (net TPV < 0) thresholds in at least one month, prioritised by how many months they were flagged.`,
    concept: `**Chained CTEs and HAVING with an aggregated flag**

Complex analytical logic is easier to read when broken into layers. Each CTE adds one piece:

1. First CTE: calculate raw numbers (gross, refunds, chargebacks) per customer-month
2. Second CTE: derive rates from those numbers
3. Third CTE: apply threshold tests and produce a binary flag (1 = risky, 0 = clean)
4. Final query: aggregate the flags per customer

The binary flag pattern is worth knowing on its own. \`CASE WHEN condition THEN 1 ELSE 0 END\` converts a boolean test into a number - then \`SUM\` counts how many times it was true:

\`\`\`sql
CASE WHEN some_rate > threshold THEN 1 ELSE 0 END AS risk_flag
-- then later:
SUM(risk_flag)  -- how many months was this customer risky?
\`\`\`

**HAVING vs WHERE:** \`WHERE\` filters rows before grouping; \`HAVING\` filters the grouped result. Here you need \`HAVING\` because the condition (\`SUM(risk_flag) > 0\`) only exists after aggregation.`,
    context: `**The Business Request**

The CFO wants to identify high-risk customers for account review. Flag any customer-month where:

- Chargeback rate > 5%
- Refund rate > 10%
- Net TPV < 0

**The Concept**

Use the customer-month dataset from Q3, add risk flags, then aggregate to find persistently risky customers.

**The Syntax**

\`\`\`sql
WITH customer_months AS (...),
flagged AS (
  SELECT *,
    CASE WHEN ... THEN 1 ELSE 0 END AS risk_flag
  FROM customer_months
)
SELECT 
  customer_id,
  SUM(risk_flag) AS flagged_months,
  SUM(...) AS total_net_tpv
FROM flagged
GROUP BY customer_id
HAVING ...
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Return: \`customer_id\`, \`flagged_months\` (count of risky months), \`total_net_tpv_gbp\`. Only include customers with at least one flagged month.

</div>`,
    hint: `**Steps:**

1. Create the customer-month CTE from Q3
2. Add a risk_flag column using CASE WHEN
3. Filter to only flagged customers
4. Aggregate: SUM(risk_flag) for count, SUM(net_tpv) for total`,
    solution: `WITH customer_months AS (
  SELECT 
    t.customer_id,
    DATE_TRUNC('month', t.transaction_time) AS month,
    SUM(t.amount * fx.rate_to_gbp) AS gross_tpv_gbp,
    COALESCE(SUM(r.refund_amount * fx.rate_to_gbp), 0) AS refund_total_gbp,
    COALESCE(SUM(c.amount * fx.rate_to_gbp), 0) AS chargeback_total_gbp,
    SUM(t.amount * fx.rate_to_gbp) - COALESCE(SUM(r.refund_amount * fx.rate_to_gbp), 0) - COALESCE(SUM(c.amount * fx.rate_to_gbp), 0) AS net_tpv_gbp
  FROM transactions t
  JOIN fx_rates fx ON DATE(t.transaction_time) = fx.date AND t.currency = fx.currency
  LEFT JOIN refunds r ON t.transaction_id = r.transaction_id
  LEFT JOIN chargebacks c ON t.transaction_id = c.transaction_id
  WHERE t.status = 'success'
  GROUP BY t.customer_id, DATE_TRUNC('month', t.transaction_time)
),
with_rates AS (
  SELECT *,
    CASE WHEN gross_tpv_gbp > 0 THEN 100.0 * chargeback_total_gbp / gross_tpv_gbp ELSE 0 END AS chargeback_rate,
    CASE WHEN gross_tpv_gbp > 0 THEN 100.0 * refund_total_gbp / gross_tpv_gbp ELSE 0 END AS refund_rate
  FROM customer_months
),
flagged AS (
  SELECT *,
    CASE WHEN chargeback_rate > 5 OR refund_rate > 10 OR net_tpv_gbp < 0 THEN 1 ELSE 0 END AS risk_flag
  FROM with_rates
)
SELECT 
  customer_id,
  SUM(risk_flag) AS flagged_months,
  ROUND(SUM(net_tpv_gbp), 2) AS total_net_tpv_gbp
FROM flagged
GROUP BY customer_id
HAVING SUM(risk_flag) > 0
ORDER BY flagged_months DESC, total_net_tpv_gbp ASC`,
    tip: `Customers with negative net TPV are effectively costing you money. Prioritize reviewing customers with multiple flagged months-one bad month may be an anomaly.`,
    explanation: `**Risk Flags Active**

**What Just Happened:**

You built a logic gate that scans every customer's history. If any month breached your safety thresholds, the customer was flagged for review.

**How Determination Logic Works:**

| Risk Type | Threshold | Risk Severity |
|-----------|-----------|---------------|
| **Chargebacks** | Rate > 5% | 🔴 **Critical** (Fraud likely) |
| **Refunds** | Rate > 10% | 🟡 **Warning** (Quality issue) |
| **Losses** | Net TPV < 0 | 🟠 **Alert** (Unprofitable) |

**Business Context:**

Manual review is expensive. You cannot review 100,000 customers. By filtering for these specific flags, you build a targeted **"Risk Queue"** of perhaps 50-100 customers for the fraud team to investigate manually. This is how modern compliance teams scale.`,
    concepts: [
      {
        icon: <FaUserShield className="text-3xl text-[#ef4444]" />,
        title: 'Risk Thresholds',
        description: 'Define clear thresholds for risky behaviour: chargeback > 5%, refund > 10%, negative margin. Flag and monitor.'
      },
      {
        icon: <FaChartLine className="text-3xl text-[#f97316]" />,
        title: 'Trend Analysis',
        description: 'Count flagged months to distinguish one-time issues from persistent problems. Prioritize action accordingly.'
      }
    ]
  },
  {
    id: 'cohort-net-tpv',
    title: 'Cohort Net TPV Analysis',
    question: `- Assign each customer a \`cohort_month\` - the month of their earliest successful transaction
- For each customer-month of activity, calculate \`months_since_start\` relative to their cohort month
- Return \`cohort_month\`, \`activity_month\`, \`months_since_start\`, \`number_of_customers\`, and \`total_net_tpv_gbp\``,
    keyConcepts: ['window functions', 'MIN()', 'cohort assignment', 'relative time'],
    defaultQuery: `-- TIP: DATE_TRUNC('month', signup_ts) labels each user's cohort month
-- JOIN back to transactions to get activity_month per cohort`,
    expectedColumns: [
      { name: 'cohort_month', type: 'DATE' },
      { name: 'activity_month', type: 'DATE' },
      { name: 'months_since_start', type: 'INTEGER' },
      { name: 'number_of_customers', type: 'INTEGER' },
      { name: 'total_net_tpv_gbp', type: 'DECIMAL' },
    ],
    businessRequest: `The CFO wants cohort analysis: how do customers acquired in different months perform over their lifetime? This reveals whether retention and revenue are improving across acquisition cohorts.`,
    concept: `**The cohort analysis pattern**

Cohort analysis answers: "do customers we acquired more recently behave differently from older ones?" The approach has two steps.

**Step 1 - assign each customer a cohort.** A cohort is just the month of their first event. \`MIN()\` finds it:

\`\`\`sql
SELECT customer_id, DATE_TRUNC('month', MIN(event_time)) AS cohort_month
FROM events
GROUP BY customer_id
\`\`\`

**Step 2 - calculate relative time.** You want \`months_since_start = 0\` in the acquisition month, \`= 1\` in the following month, and so on. This lets you compare a January cohort and a June cohort side by side on the same relative timeline.

\`\`\`sql
(EXTRACT(YEAR  FROM activity_month) - EXTRACT(YEAR  FROM cohort_month)) * 12
+ EXTRACT(MONTH FROM activity_month) - EXTRACT(MONTH FROM cohort_month)
\`\`\`

Once you have cohort_month and activity_month on the same row (via a JOIN), \`GROUP BY cohort_month, activity_month\` gives you one cell per cohort per period - the classic cohort grid.`,
    context: `**The Business Request**

The CFO wants cohort analysis: How do customers acquired in different months perform over their lifetime?

**The Concept**

Cohort analysis tracks groups of customers who share a common characteristic (here: first transaction month) over time.

1. **Assign cohort**: MIN(transaction_month) per customer
2. **Calculate relative month**: months since cohort start
3. **Aggregate**: net TPV by cohort × activity month

**The Syntax**

\`\`\`sql
WITH customer_cohorts AS (
  SELECT customer_id, MIN(...) AS cohort_month
  FROM transactions 
  GROUP BY customer_id
),
customer_activity AS (...)
SELECT 
  cohort_month, 
  activity_month,
  ... AS months_since_start,
  COUNT(DISTINCT ...) AS customers,
  SUM(...) AS total_net_tpv
FROM customer_activity
JOIN customer_cohorts USING (customer_id)
GROUP BY cohort_month, activity_month
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Return: \`cohort_month\`, \`activity_month\`, \`months_since_start\`, \`number_of_customers\`, \`total_net_tpv_gbp\`.

</div>`,
    hint: `**Key insight:**

Calculate months_since_start as EXTRACT(YEAR FROM activity_month - cohort_month) * 12 + EXTRACT(MONTH FROM activity_month - cohort_month) for accurate month difference.`,
    solution: `WITH customer_cohorts AS (
  SELECT 
    customer_id,
    DATE_TRUNC('month', MIN(transaction_time)) AS cohort_month
  FROM transactions
  WHERE status = 'success'
  GROUP BY customer_id
),
customer_months AS (
  SELECT 
    t.customer_id,
    DATE_TRUNC('month', t.transaction_time) AS activity_month,
    SUM(t.amount * fx.rate_to_gbp) - COALESCE(SUM(r.refund_amount * fx.rate_to_gbp), 0) - COALESCE(SUM(c.amount * fx.rate_to_gbp), 0) AS net_tpv_gbp
  FROM transactions t
  JOIN fx_rates fx ON DATE(t.transaction_time) = fx.date AND t.currency = fx.currency
  LEFT JOIN refunds r ON t.transaction_id = r.transaction_id
  LEFT JOIN chargebacks c ON t.transaction_id = c.transaction_id
  WHERE t.status = 'success'
  GROUP BY t.customer_id, DATE_TRUNC('month', t.transaction_time)
)
SELECT 
  cc.cohort_month,
  cm.activity_month,
  (EXTRACT(YEAR FROM cm.activity_month) - EXTRACT(YEAR FROM cc.cohort_month)) * 12 +
    (EXTRACT(MONTH FROM cm.activity_month) - EXTRACT(MONTH FROM cc.cohort_month)) AS months_since_start,
  COUNT(DISTINCT cm.customer_id) AS number_of_customers,
  ROUND(SUM(cm.net_tpv_gbp), 2) AS total_net_tpv_gbp
FROM customer_months cm
JOIN customer_cohorts cc ON cm.customer_id = cc.customer_id
GROUP BY cc.cohort_month, cm.activity_month
ORDER BY cc.cohort_month, cm.activity_month`,
    tip: `Cohort analysis reveals if customer quality is improving over time. If recent cohorts perform worse, investigate acquisition channel changes.`,
    explanation: `**Cohort Grid Created**

**What Just Happened:**

You successfully transformed linear transaction data into a cohort grid. You can now see exactly how a group of customers acquired in January behaved in February, March, and April.

**How It Works:**

| Concept | Definition | Example |
|---------|------------|---------|
| **Cohort Month** | The "Birth Month" of the user | User joins in **Jan** |
| **Activity Month** | When they transacted | User buys in **March** |
| **Relative Month** | Distance from start | March is **Month 2** for Jan cohort |

**Business Context:**

**Retention is King.**
If Month 0 Net TPV is £100k, but Month 6 is only £10k, you have high churn.
If Month 6 is £150k, you have "Negative Churn" (Net Expansion)-the holy grail of SaaS and Fintech growth.`,
    concepts: [
      {
        icon: <FaUsers className="text-3xl text-[#2563eb]" />,
        title: 'Cohort Assignment',
        description: 'Group customers by their first transaction month. This enables tracking performance over relative time.'
      },
      {
        icon: <FaLayerGroup className="text-3xl text-[#10b981]" />,
        title: 'Relative Time',
        description: 'Calculate months since cohort start to compare cohorts fairly. Month 3 for all cohorts represents their third month of activity.'
      }
    ]
  },
  {
    id: 'cohort-quality-comparison',
    title: 'Cohort Quality Comparison',
    question: `- Build on the Q5 cohort CTE; filter to \`months_since_start <= 2\` (first 3 months) and GROUP BY \`cohort_month\`
- \`avg_customer_value\` = cumulative net TPV ÷ distinct customers in the cohort
- Return \`cohort_month\`, \`cumulative_net_tpv_first_3m\`, \`avg_customer_value\`, and \`performance_rank\` (rank cohorts by cumulative net TPV descending using a window function)`,
    keyConcepts: ['HAVING', 'cumulative metrics', 'RANK()', 'cohort comparison'],
    defaultQuery: `-- TIP: Use a CTE to limit to the first 3 months of activity per cohort
-- WHERE DATEDIFF('month', cohort_month, activity_month) < 3`,
    expectedColumns: [
      { name: 'cohort_month', type: 'DATE' },
      { name: 'cumulative_net_tpv_first_3m', type: 'DECIMAL' },
      { name: 'avg_customer_value', type: 'DECIMAL' },
      { name: 'performance_rank', type: 'INTEGER' },
    ],
    businessRequest: `The CFO wants to rank cohort quality using a standardised window. By comparing only the first 3 months, newer cohorts can be evaluated on equal footing with older ones.`,
    concept: `**Fixed comparison windows + RANK()**

**The fixed window principle.** A January cohort has more calendar months of history than a September cohort. To make a fair comparison, restrict every cohort to the same relative window - e.g. months 0, 1, 2 only. Then \`SUM()\` aggregates on identical terms:

\`\`\`sql
WHERE months_since_start <= 2
GROUP BY cohort_month
\`\`\`

**RANK() as a post-aggregation layer.** After \`GROUP BY\` produces one row per cohort, \`RANK()\` adds an ordinal column without collapsing or re-grouping those rows:

\`\`\`sql
RANK() OVER (ORDER BY total_value DESC) AS performance_rank
\`\`\`

\`RANK()\` is a window function: it "looks across" the already-aggregated result set and assigns 1 to the highest value, 2 to the next, and so on. Ties get the same rank, and the next rank after a tie skips a number (1, 2, 2, 4...).

**Reading the output.** A cohort with high cumulative value but low \`avg_customer_value\` was large but individually modest. The inverse - smaller cohort, high average - signals high-value acquisitions. Both metrics together tell the full story.`,
    context: `**The Business Request**

The CFO wants to compare cohort quality using standardized metrics. By looking at only the first 3 months, we can fairly compare cohorts even if some are newer.

**The Concept**

- Filter to \`months_since_start <= 2\` (months 0, 1, 2)
- Calculate cumulative net TPV
- Calculate average customer value
- Rank cohorts by performance

**The Syntax**

\`\`\`sql
SELECT 
  cohort_month,
  SUM(...) AS cumulative_net_tpv_first_3m,
  SUM(...) / COUNT(DISTINCT ...) AS avg_customer_value,
  RANK() OVER (ORDER BY ... DESC) AS performance_rank
FROM cohort_data
WHERE months_since_start <= 2
GROUP BY cohort_month
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Return: \`cohort_month\`, \`cumulative_net_tpv_first_3m\`, \`avg_customer_value\`, \`performance_rank\`.

</div>`,
    hint: `**Steps:**

1. Use the cohort query from Q5 as a base
2. Filter WHERE months_since_start <= 2
3. GROUP BY cohort_month only
4. Use RANK() OVER (ORDER BY cumulative_net_tpv DESC) for ranking`,
    solution: `WITH customer_cohorts AS (
  SELECT 
    customer_id,
    DATE_TRUNC('month', MIN(transaction_time)) AS cohort_month
  FROM transactions
  WHERE status = 'success'
  GROUP BY customer_id
),
customer_months AS (
  SELECT 
    t.customer_id,
    DATE_TRUNC('month', t.transaction_time) AS activity_month,
    SUM(t.amount * fx.rate_to_gbp) - COALESCE(SUM(r.refund_amount * fx.rate_to_gbp), 0) - COALESCE(SUM(c.amount * fx.rate_to_gbp), 0) AS net_tpv_gbp
  FROM transactions t
  JOIN fx_rates fx ON DATE(t.transaction_time) = fx.date AND t.currency = fx.currency
  LEFT JOIN refunds r ON t.transaction_id = r.transaction_id
  LEFT JOIN chargebacks c ON t.transaction_id = c.transaction_id
  WHERE t.status = 'success'
  GROUP BY t.customer_id, DATE_TRUNC('month', t.transaction_time)
),
cohort_activity AS (
  SELECT 
    cc.cohort_month,
    cm.customer_id,
    cm.net_tpv_gbp,
    (EXTRACT(YEAR FROM cm.activity_month) - EXTRACT(YEAR FROM cc.cohort_month)) * 12 +
      (EXTRACT(MONTH FROM cm.activity_month) - EXTRACT(MONTH FROM cc.cohort_month)) AS months_since_start
  FROM customer_months cm
  JOIN customer_cohorts cc ON cm.customer_id = cc.customer_id
)
SELECT 
  cohort_month,
  ROUND(SUM(net_tpv_gbp), 2) AS cumulative_net_tpv_first_3m,
  ROUND(SUM(net_tpv_gbp) / COUNT(DISTINCT customer_id), 2) AS avg_customer_value,
  RANK() OVER (ORDER BY SUM(net_tpv_gbp) DESC) AS performance_rank
FROM cohort_activity
WHERE months_since_start <= 2
GROUP BY cohort_month
ORDER BY performance_rank`,
    tip: `Use first 3 months to fairly compare cohorts-newer cohorts haven't had time to mature. This standardizes the comparison window.`,
    explanation: `**Cohort Performance Ranked**

**What Just Happened:**

You standardized the comparison window to the first 3 months. This allows you to compare a mature cohort (from last year) fairly against a brand new cohort (from 3 months ago).

**How It Works:**

| Rank Metric | Why Use It? |
|-------------|-------------|
| **Cumulative Net TPV** | Total value generated. Good for spotting "Whale" cohorts. |
| **Avg Customer Value** | Value per user. Good for spotting "High Quality" cohorts. |

**Business Context:**

Not all growth is good growth.
If your marketing team doubled lead volume in Q4, but this query shows Q4 cohorts have half the \`avg_customer_value\` of Q3, then the quality of leads has dropped. You solved the volume problem but created a quality problem. Existing this query helps executives spot that trade-off immediately.`,
    concepts: [
      {
        icon: <FaTrophy className="text-3xl text-[#f97316]" />,
        title: 'Cohort Ranking',
        description: 'Use RANK() to compare cohort performance. Identify which acquisition periods brought the highest-value customers.'
      },
      {
        icon: <FaChartLine className="text-3xl text-[#2563eb]" />,
        title: 'Standardized Windows',
        description: 'Compare first 3 months only to fairly evaluate cohorts. Newer cohorts can be compared to older ones on equal footing.'
      }
    ]
  }
];

export const fintechConcepts = [
  {
    icon: <FaExchangeAlt className="text-3xl text-[#2563eb]" />,
    title: 'Currency Normalization',
    description: 'Join to FX rates on date AND currency to convert multi-currency transactions to a single reporting currency (GBP).'
  },
  {
    icon: <FaCalculator className="text-3xl text-[#10b981]" />,
    title: 'Net TPV Calculation',
    description: 'Net TPV = Gross TPV - Refunds - Chargebacks. This is the actual revenue retained by the payment platform.'
  },
  {
    icon: <FaUserShield className="text-3xl text-[#ef4444]" />,
    title: 'Risk Analysis',
    description: 'Monitor chargeback and refund rates. Rates > 1% chargeback can trigger card network penalties.'
  },
  {
    icon: <FaUsers className="text-3xl text-[#f97316]" />,
    title: 'Cohort Analysis',
    description: 'Group customers by first transaction month and track relative performance. Essential for understanding customer quality trends.'
  }
];
